import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle } from 'lucide-react';
import SeoHead from './SeoHead';
import FAQSection from './FAQSection';
import { venueSupportPackages } from '../data/ubuntuServices';
import {
  loadEstimateSelections,
  setVenueSupportPackage,
} from '../utils/estimateSelections';

const WHATSAPP_NUMBER = '27663286231';
const faqItems = [
  { q: 'Do you help with venue booking in Pretoria?', a: 'Yes, we support venue booking in Pretoria and Gauteng including Centurion, Midrand, and Johannesburg.' },
  { q: 'Can you combine venue support with other services?', a: 'Yes, we can align venue support with catering, decor, sound, and equipment hire.' },
  { q: 'Do you assist with indoor and outdoor venues?', a: 'Yes, we assist with both indoor and outdoor event requirements.' },
  { q: 'How does pricing work?', a: 'Venue support packages are quoted clearly and confirmed after event brief review.' },
  { q: 'How quickly can I submit details?', a: 'You can submit your brief directly through WhatsApp in 1-2 clicks.' },
];

const openWhatsApp = (message) => {
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    message
  )}`;
  window.open(whatsappUrl, '_blank');
};

const goToEstimate = () => {
  window.dispatchEvent(new CustomEvent('navigate', { detail: 'estimate' }));
};

const VenueSupport = () => {
  const hireItems = [
    {
      id: 'tents',
      title: 'Tents for Hire',
      image:
        'https://images.unsplash.com/photo-1520854221256-17451cc331bf?auto=format&fit=crop&w=1400&q=80',
      description:
        'Suitable for outdoor celebrations, private functions, community events, and corporate gatherings.',
    },
    {
      id: 'chairs',
      title: 'Chairs for Hire',
      image:
        'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&w=1400&q=80',
      description:
        'Available for weddings, parties, conferences, and all types of indoor or outdoor events.',
    },
    {
      id: 'tables',
      title: 'Tables for Hire',
      image:
        'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1400&q=80',
      description:
        'Ideal for dining setups, serving stations, registration areas, and event seating arrangements.',
    },
  ];
  const [brief, setBrief] = useState({
    eventType: '',
    preferredArea: '',
    indoorOutdoor: 'indoor',
    guestCount: '',
    preferredDate: '',
    budgetRange: '',
    notes: '',
  });
  const [hireQuantities, setHireQuantities] = useState({
    tents: 0,
    chairs: 0,
    tables: 0,
  });

  const sendVenueRequest = () => {
    const message = `Hi UBUNTU EVENTS MANAGEMENT,\n\nI'm requesting venue booking support.\n\nEvent brief:\n- Event type: ${
      brief.eventType || 'Not provided'
    }\n- Preferred area: ${brief.preferredArea || 'Not provided'}\n- Indoor/Outdoor: ${
      brief.indoorOutdoor || 'Not provided'
    }\n- Guest count: ${brief.guestCount || 'Not provided'}\n- Preferred date: ${
      brief.preferredDate || 'Not provided'
    }\n- Budget range: ${brief.budgetRange || 'Not provided'}\n- Notes: ${
      brief.notes || 'None'
    }\n\nPlease share suitable venue options and advise next steps.\nThank you!`;

    openWhatsApp(message);
  };

  const sendHireQuoteRequest = (itemId) => {
    const selections = loadEstimateSelections();
    const context = selections.estimateEventContext || {};
    const selectedItem = hireItems.find((item) => item.id === itemId);

    const message = `Hi UBUNTU EVENTS MANAGEMENT, I would like a personalized quotation for the following hire items:\n- Chairs: ${
      hireQuantities.chairs || 0
    }\n- Tables: ${hireQuantities.tables || 0}\n- Tents: ${
      hireQuantities.tents || 0
    }\n\nSelected item: ${selectedItem?.title || 'Not specified'}\nRequested quantity for selected item: ${
      hireQuantities[itemId] || 0
    }\n${
      context.eventDate
        ? `\nEvent date: ${context.eventDate}`
        : ''
    }${
      context.location
        ? `\nLocation: ${context.location}`
        : ''
    }\n\nPlease assist with availability and a quotation for my event.`;

    openWhatsApp(message);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream to-white py-12 px-4">
      <SeoHead
        title="Venue Booking Pretoria | Ubuntu Enterprises"
        description="Venue booking support in Pretoria and Gauteng with streamlined WhatsApp event brief submission."
        canonicalPath="/venuesupport"
        faqSchema={faqItems}
      />
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[480px] flex items-center justify-center overflow-hidden rounded-2xl">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              "linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url('https://images.unsplash.com/photo-1520962917966-7e8bb4f0b9b9?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80')",
          }}
        />
        <div className="hero-overlay" />
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-playfair font-bold mb-6"
          >
            Venue Booking Support Made Simple
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-2xl font-light text-white/90"
          >
            We help you identify, compare, and secure the right venue for your
            event based on your style, location, budget, and guest count.
          </motion.p>
        </div>
      </section>

      {/* Service Cards */}
      <section className="mt-14">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-800 mb-4">
              Venue Support Options
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Some venue support is charged as a flat fee, and others can be
              built into full-event coordination. For now, we display
              flat, market-related placeholder pricing.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {venueSupportPackages.map((pkg, idx) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.06 }}
                viewport={{ once: true }}
                className="card group hover:shadow-2xl flex flex-col"
              >
                <div className="aspect-w-16 aspect-h-9 overflow-hidden rounded-xl mb-5">
                  <img
                    src={pkg.image}
                    alt={`${pkg.name} venue booking support Gauteng`}
                    className="w-full h-40 sm:h-44 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                <div className="flex-1">
                  <h3 className="text-2xl font-playfair font-semibold text-gray-800 mb-2">
                    {pkg.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-5">
                    {pkg.description}
                  </p>

                  <div className="flex items-baseline justify-between mb-4">
                    <span className="text-3xl font-bold text-gold">
                      R{pkg.price.toLocaleString()}
                    </span>
                    <span className="text-sm text-gray-500">placeholder</span>
                  </div>

                  <ul className="text-sm text-gray-700 space-y-2">
                    {pkg.features.map((f, i) => (
                      <li key={`${pkg.id}-f-${i}`} className="flex items-center">
                        <span className="text-gold mr-2">✓</span>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-3 mt-6">
                  <button
                    onClick={() => {
                      setVenueSupportPackage(pkg.id);
                      goToEstimate();
                    }}
                    className="w-full btn-primary text-sm py-3"
                  >
                    Add to Estimate
                  </button>
                  <button
                    onClick={() =>
                      openWhatsApp(
                        `Hi UBUNTU EVENTS MANAGEMENT,\n\nI'd like to get a quote for venue support:\n\n${pkg.name}\nPrice: ${pkg.priceLabel || `R${pkg.price}`}\n\nPlease confirm availability and final pricing.\nThank you!`
                      )
                    }
                    className="w-full btn-secondary text-sm py-3"
                  >
                    <MessageCircle className="w-4 h-4 inline mr-2" />
                    WhatsApp Quote
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Event Furniture & Tent Hire */}
      <section className="mt-14">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-800 mb-4">
              Event Furniture & Tent Hire
            </h2>
            <p className="text-lg md:text-xl text-olive font-medium mb-4">
              Tents, chairs, and tables available for hire for indoor and outdoor
              events.
            </p>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              UBUNTU EVENTS MANAGEMENT also offers event hire items to support your
              function setup. Whether you need a few essentials or larger event
              quantities, we can assist with tent, chair, and table hire based
              on your event requirements.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {hireItems.map((item, idx) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.06 }}
                viewport={{ once: true }}
                className="card group hover:shadow-2xl flex flex-col bg-gradient-to-br from-white to-cream/40 border border-gold/15"
              >
                <div className="overflow-hidden rounded-xl mb-5">
                  <img
                    src={item.image}
                    alt={`${item.title} event hire in Gauteng`}
                    className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                <div className="flex-1">
                  <h3 className="text-2xl font-playfair font-semibold text-gray-800 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">{item.description}</p>
                </div>

                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantity needed
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={hireQuantities[item.id]}
                    onChange={(e) =>
                      setHireQuantities((prev) => ({
                        ...prev,
                        [item.id]: Math.max(0, parseInt(e.target.value, 10) || 0),
                      }))
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                    placeholder="Enter quantity"
                  />
                </div>

                <button
                  onClick={() => sendHireQuoteRequest(item.id)}
                  className="w-full btn-secondary text-sm py-3 mt-4"
                >
                  <MessageCircle className="w-4 h-4 inline mr-2" />
                  Get Personalized Quote
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Venue Brief Form */}
      <section className="mt-14">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-800 mb-4">
              Share Your Venue Brief
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Tell us what you're planning and we'll guide you toward suitable
              venues.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="card"
            >
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendVenueRequest();
                }}
                className="space-y-5"
              >
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Event Type
                    </label>
                    <select
                      value={brief.eventType}
                      onChange={(e) =>
                        setBrief((p) => ({ ...p, eventType: e.target.value }))
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                    >
                      <option value="">Select event type</option>
                      <option value="Wedding">Wedding</option>
                      <option value="Birthday">Birthday</option>
                      <option value="Corporate Event">Corporate Event</option>
                      <option value="Team Building">Team Building</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Area
                    </label>
                    <input
                      value={brief.preferredArea}
                      onChange={(e) =>
                        setBrief((p) => ({
                          ...p,
                          preferredArea: e.target.value,
                        }))
                      }
                      placeholder="e.g. Pretoria West, Centurion..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Indoor / Outdoor
                    </label>
                    <select
                      value={brief.indoorOutdoor}
                      onChange={(e) =>
                        setBrief((p) => ({
                          ...p,
                          indoorOutdoor: e.target.value,
                        }))
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                    >
                      <option value="indoor">Indoor</option>
                      <option value="outdoor">Outdoor</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Guests
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={brief.guestCount}
                      onChange={(e) =>
                        setBrief((p) => ({
                          ...p,
                          guestCount: e.target.value,
                        }))
                      }
                      placeholder="e.g. 80"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Date
                    </label>
                    <input
                      type="date"
                      value={brief.preferredDate}
                      onChange={(e) =>
                        setBrief((p) => ({
                          ...p,
                          preferredDate: e.target.value,
                        }))
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Budget Range
                    </label>
                    <input
                      value={brief.budgetRange}
                      onChange={(e) =>
                        setBrief((p) => ({
                          ...p,
                          budgetRange: e.target.value,
                        }))
                      }
                      placeholder="e.g. R10k - R25k"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    rows="4"
                    value={brief.notes}
                    onChange={(e) =>
                      setBrief((p) => ({
                        ...p,
                        notes: e.target.value,
                      }))
                    }
                    placeholder="Any special requirements, preferences, or questions..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent resize-none"
                  />
                </div>

                <button type="submit" className="btn-primary w-full py-4 text-lg">
                  <MessageCircle className="w-5 h-5 inline mr-2" />
                  Send Venue Request via WhatsApp
                </button>
              </form>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="card bg-gradient-to-br from-gold/10 to-olive/10 border-2 border-gold/20"
            >
              <h3 className="text-2xl font-playfair font-bold text-gray-800 mb-4">
                What happens next?
              </h3>
              <div className="space-y-4 text-gray-700">
                <div className="flex items-start space-x-3">
                  <span className="text-gold font-bold">1</span>
                  <p>
                    We review your brief and suggest suitable venues that match
                    your location, style, and budget.
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-gold font-bold">2</span>
                  <p>
                    If you choose booking assistance, we handle communication
                    and coordinate the availability confirmation.
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-gold font-bold">3</span>
                  <p>
                    With full coordination, we help align venue timing with
                    your other event services like sound, catering, and decor.
                  </p>
                </div>
              </div>

              <div className="mt-8">
                <button
                  onClick={() => {
                    setVenueSupportPackage(venueSupportPackages[0]?.id);
                    goToEstimate();
                  }}
                  className="btn-secondary w-full py-4 text-lg"
                >
                  Add Venue Search to Estimate
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      <FAQSection title="Venue Support FAQs" faqs={faqItems} />
    </div>
  );
};

export default VenueSupport;

